import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getPersistStorage } from "@/utils/persist-storage";

export type DevicePrinterPaperWidth = 58 | 80;

export type DevicePrinter =
  | {
      kind: "bluetooth";
      /**
       * Plugin device identifier — a MAC address on Android, a peripheral UUID
       * on iOS. Stable per physical device, which is exactly the scope we want:
       * each device remembers its own printer.
       */
      deviceId: string;
      name: string;
      paperWidth: DevicePrinterPaperWidth;
    }
  | {
      /**
       * The thermal printer built into a Sunmi POS device. Its paper width is
       * fixed by the hardware and cannot be queried over the vendor service,
       * so unlike a Bluetooth printer it carries no width to configure.
       */
      kind: "sunmi-internal";
      name: string;
    };

/** What version 0 persisted, back when a Bluetooth printer was the only option. */
type LegacyBluetoothOnlyState = {
  selectedPrinter: {
    deviceId: string;
    name: string;
    paperWidth: DevicePrinterPaperWidth;
  } | null;
};

type DevicePrinterStore = {
  selectedPrinter: DevicePrinter | null;
  setSelectedPrinter: (printer: DevicePrinter | null) => void;
  setPaperWidth: (paperWidth: DevicePrinterPaperWidth) => void;
};

/**
 * Device-local choice of the receipt printer used by the mobile app — either a
 * paired Bluetooth printer or the built-in printer of a Sunmi POS device.
 * Persisted in localStorage (which Capacitor keeps per install), mirroring the
 * existing per-device POS preferences (`pos-businessPremiseId`).
 */
export const useDevicePrinterStore = create<DevicePrinterStore>()(
  persist(
    (set, get) => ({
      selectedPrinter: null,
      setSelectedPrinter: (printer) => set({ selectedPrinter: printer }),
      setPaperWidth: (paperWidth) => {
        const selectedPrinter = get().selectedPrinter;
        if (selectedPrinter?.kind !== "bluetooth") return;
        set({ selectedPrinter: { ...selectedPrinter, paperWidth } });
      },
    }),
    {
      // Keeps the key the Bluetooth-only version wrote, so installs that
      // already have a printer selected do not silently lose it.
      name: "bt-printer",
      version: 1,
      migrate: (persistedState, version) => {
        if (version === 0) {
          const legacy = persistedState as LegacyBluetoothOnlyState | undefined;

          if (legacy?.selectedPrinter != null) {
            return {
              ...legacy,
              selectedPrinter: {
                kind: "bluetooth",
                ...legacy.selectedPrinter,
              },
            } as DevicePrinterStore;
          }
        }

        return persistedState as DevicePrinterStore;
      },
      storage: createJSONStorage(getPersistStorage),
    },
  ),
);
